/**
 * VersionHistory Component
 * Displays version history for a test scenario
 */

import React, { useState, useEffect } from 'react';
import { apiClient, TestScenario } from '../services/api';
import { emailToUsername } from '../utils/formatters';
import './VersionHistory.css';

interface VersionHistoryProps {
  scenarioId: string;
  onVersionSelect?: (version: TestScenario) => void;
  onClose?: () => void;
}

export const VersionHistory: React.FC<VersionHistoryProps> = ({
  scenarioId,
  onVersionSelect,
  onClose,
}) => {
  const [versions, setVersions] = useState<TestScenario[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<TestScenario | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load version history for the scenario
   */
  const loadVersionHistory = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiClient.getScenarioVersions(scenarioId);
      // Sort by version number descending (newest first)
      const sortedVersions = data.sort((a, b) => b.version - a.version);
      setVersions(sortedVersions);
      
      // Auto-select the latest version
      if (sortedVersions.length > 0) {
        setSelectedVersion(sortedVersions[0]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load version history');
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadVersionHistory();
  }, [scenarioId]);

  /**
   * Handle version selection
   */
  const handleVersionClick = (version: TestScenario) => {
    setSelectedVersion(version);
    onVersionSelect?.(version);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * Get priority badge class
   */
  const getPriorityClass = (priority: string): string => {
    switch (priority) {
      case 'Critical':
        return 'priority-critical';
      case 'High':
        return 'priority-high';
      case 'Medium':
        return 'priority-medium';
      case 'Low':
        return 'priority-low';
      default:
        return '';
    }
  };

  return (
    <div className="version-history">
      <div className="version-history-header">
        <h2>Version History</h2>
        {onClose && (
          <button
            className="btn-close"
            onClick={onClose}
            aria-label="Close version history"
          >
            ×
          </button>
        )}
      </div>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="loading">Loading version history...</div>
      ) : versions.length === 0 ? (
        <div className="empty-state">
          <p>No version history available.</p>
        </div>
      ) : (
        <div className="version-history-content">
          {/* Version Timeline */}
          <div className="version-timeline">
            <h3>Versions ({versions.length})</h3>
            <div className="version-list">
              {versions.map((version) => (
                <div
                  key={version.id}
                  className={`version-item ${
                    selectedVersion?.id === version.id ? 'selected' : ''
                  }`}
                  onClick={() => handleVersionClick(version)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleVersionClick(version);
                    }
                  }}
                >
                  <div className="version-number">
                    v{version.version}
                    {version.is_latest && (
                      <span className="latest-badge">Latest</span>
                    )}
                  </div>
                  <div className="version-meta">
                    <span className="version-date">
                      {formatDate(version.updated_at)}
                    </span>
                    <span className="version-author">by {version.updated_by}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Version Details */}
          {selectedVersion && (
            <div className="version-details">
              <div className="version-details-header">
                <h3>Version {selectedVersion.version} Details</h3>
                <span className={`priority-badge ${getPriorityClass(selectedVersion.priority)}`}>
                  {selectedVersion.priority}
                </span>
              </div>

              <div className="detail-section">
                <h4>Description</h4>
                <p>{selectedVersion.scenario_description}</p>
              </div>

              {selectedVersion.preconditions && (
                <div className="detail-section">
                  <h4>Preconditions</h4>
                  <p>{selectedVersion.preconditions}</p>
                </div>
              )}

              <div className="detail-section">
                <h4>Expected Result</h4>
                <p>{selectedVersion.expected_result}</p>
              </div>

              {selectedVersion.module_name && (
                <div className="detail-section">
                  <h4>Module/Feature</h4>
                  <p>{selectedVersion.module_name}</p>
                </div>
              )}

              {selectedVersion.tags.length > 0 && (
                <div className="detail-section">
                  <h4>Tags</h4>
                  <div className="tag-list">
                    {selectedVersion.tags.map((tag) => (
                      <span key={tag} className="tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="detail-section">
                <h4>Metadata</h4>
                <div className="metadata-grid">
                  <div className="metadata-item">
                    <span className="metadata-label">Created:</span>
                    <span className="metadata-value">
                      {formatDate(selectedVersion.created_at)}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Created by:</span>
                    <span className="metadata-value">
                      {emailToUsername(selectedVersion.created_by)}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Last updated:</span>
                    <span className="metadata-value">
                      {formatDate(selectedVersion.updated_at)}
                    </span>
                  </div>
                  <div className="metadata-item">
                    <span className="metadata-label">Updated by:</span>
                    <span className="metadata-value">
                      {selectedVersion.updated_by}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
